import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useUpdateTaskListPreferencesMutation } from "@/mutations/task-list-mutations";
import { useSuspenseTaskListPreferences } from "@/queries/list-queries";
import type { Attribute } from "@/types/attribute";
import type { ColumnDef } from "./ColumnModel";
import { useTaskQueryParams } from "./TaskQueryParamsContext";

interface SortableHeaderProps {
    column: ColumnDef;
    children: React.ReactNode;
    isRight: boolean;
    draggedColumnCssWidth?: string;
}

function SortableHeader({ column, children, isRight }: SortableHeaderProps) {
    const headerRef = useRef<HTMLDivElement>(null);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.id,
    });

    // Helper function to convert cssColDef to a pixel width for dragging
    const getCssWidthForDragging = (cssColDef: string): string | undefined => {
        // Handle simple pixel values like "160px"
        if (cssColDef.endsWith("px")) {
            return cssColDef;
        }

        // Handle minmax() values - use the minimum value
        const minmaxMatch = cssColDef.match(/minmax\(([^,]+),/);
        if (minmaxMatch) {
            const minValue = minmaxMatch[1].trim();
            if (minValue.endsWith("px")) {
                return minValue;
            }
        }

        // For other cases, fall back to measuring the DOM
        return undefined;
    };

    // Get the width to use during dragging
    const dragWidth = isDragging && column.cssColDef ? getCssWidthForDragging(column.cssColDef) : undefined;

    const style: CSSProperties = {
        position: "relative",
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition,
        whiteSpace: "nowrap",
        width: dragWidth,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <div
            ref={(node) => {
                setNodeRef(node);
                headerRef.current = node;
            }}
            style={style}
            {...attributes}
            {...listeners}
            className={cn(
                "px-3 py-2.5 text-left text-sm font-medium text-zinc-800 border-b border-zinc-300 cursor-grab active:cursor-grabbing bg-neutral-50",
                isRight && "text-right",
                isDragging && "bg-zinc-100"
            )}
        >
            {children}
        </div>
    );
}

function StaticTitleHeader({ children }: { children: React.ReactNode }) {
    return (
        <div className="px-3 pl-5 py-2.5 text-left text-sm font-medium text-zinc-800 border-b border-zinc-300 bg-neutral-50">
            {children}
        </div>
    );
}

function AttributeHeader({ column }: { column: ColumnDef }) {
    return (
        <div key={column.id} data-sortable-id={column.id}>
            <SortableHeader column={column} isRight={column.isRight}>
                {column.header}
            </SortableHeader>
        </div>
    );
}

export default function TaskTableHeader({ columns }: { columns: ColumnDef[] }) {
    const queryParams = useTaskQueryParams();
    const { type, taskListId } = queryParams;
    const { preferences } = useSuspenseTaskListPreferences({
        taskListId: taskListId ? Number(taskListId) : undefined,
        type,
    });
    const { mutate: updatePreferences } = useUpdateTaskListPreferencesMutation();
    const [sortedColumns, setSortedColumns] = useState<ColumnDef[]>(columns);

    useEffect(() => {
        const otherColumns = columns.slice(1);
        setSortedColumns(otherColumns);
    }, [columns]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            // Clear the dragged column width

            const attributePreferences = preferences["taskList.attributes"] as Attribute[];

            if (!event.active || !event.over) return;
            if (event.active.id === event.over.id) return;

            const oldIndex = sortedColumns.findIndex((item) => item.id.toString() === event.active.id.toString());
            const newIndex = sortedColumns.findIndex((item) => item.id.toString() === event.over!.id.toString());

            if (oldIndex !== -1 && newIndex !== -1) {
                // Create the new order array
                const reorderedData = arrayMove(sortedColumns, oldIndex, newIndex);
                const newOrderIds = reorderedData.map((item) => item.id);

                // Update the server with the new order
                setSortedColumns(reorderedData); // local state update to persist the order. don't do that via react query or we get weird in-between renders
                //updateTaskListOrder({ orderedIds: newOrderIds });

                // derive new preferences from the column order
                const newPreferences = {
                    ...preferences,
                    // we compare strings on the next line because the ID can be a custom attr. id (number) or a built in one which is a string
                    "taskList.attributes": newOrderIds.map((id) =>
                        attributePreferences.find((attribute) => attribute.id.toString() === id)
                    ),
                };

                updatePreferences({
                    taskListId: taskListId,
                    type,
                    preferences: newPreferences,
                });
            }
        },
        [sortedColumns, updatePreferences, preferences, taskListId, type]
    );

    const sortableItems = useMemo(() => sortedColumns.map((col) => col.id.toString()), [sortedColumns]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToHorizontalAxis]}
        >
            <div className="sticky top-0 flex z-20 w-full">
                <div className="w-full grid grid-cols-(--template-columns) select-none">
                    <StaticTitleHeader>Title</StaticTitleHeader>

                    {/* Render other headers as sortable */}
                    <SortableContext items={sortableItems} strategy={horizontalListSortingStrategy}>
                        {sortedColumns.map((col) => (
                            <AttributeHeader key={col.id} column={col} />
                        ))}
                    </SortableContext>
                </div>
            </div>
        </DndContext>
    );
}
